import { type Connection, PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import { CpSwapValidationError } from '../accounts/cp-swap-validation.js';
import { fetchGraduatedPoolInfoByPool, fetchOutcome } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { ASSOCIATED_TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { getCpSwapProgram } from '../idl/loader.js';
import { deriveAmmAuthorityPda } from '../pda/cp-swap.js';
import { deriveEventAuthorityPda, deriveOutcomePda } from '../pda/derivations.js';
import type { BuildResult } from '../types/build-result.js';
import type { AmmSwapParams } from '../types/trading.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  uniqueSignerKeys,
} from './common.js';

const U64_MAX: BN = new BN('18446744073709551615');

function deriveAta(owner: PublicKey, mint: PublicKey, tokenProgram: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

/**
 * Build a swap on a graduated market's cp-swap pool (Raydium CPMM fork) via the
 * `swap_base_input` instruction. This runs ENTIRELY in the cp-swap program —
 * the markets program (and its buy/prize-pool fees) is never touched. The only
 * fee is the pool's swap fee, taken from the input by the on-chain math.
 *
 * `direction: 'buy'` swaps USDC→outcome (`amountIn` = USDC, `minAmountOut` =
 * min outcome tokens); `direction: 'sell'` swaps outcome→USDC (`amountIn` =
 * outcome tokens, `minAmountOut` = min USDC). All amounts are D6 base units.
 *
 * Pool identity comes from the persistent Outcome PDA and the PoolState it
 * records. Neither the Market nor its retired bonding curves are required.
 *
 * @param connection - Solana RPC connection.
 * @param params - Swap parameters. See {@link AmmSwapParams}.
 * @returns BuildResult with an unsigned transaction ready for signing.
 */
export async function buildAmmSwap(
  connection: Connection,
  params: AmmSwapParams,
): Promise<BuildResult> {
  const amountIn = validatedU64(params.amountIn, 'amount_in_out_of_range');
  if (amountIn.isZero()) throw new CpSwapValidationError('amount_in_out_of_range');
  const minAmountOut = validatedU64(params.minAmountOut, 'min_amount_out_out_of_range');
  if (
    !Number.isSafeInteger(params.outcomeIndex) ||
    params.outcomeIndex < 0 ||
    params.outcomeIndex > 255
  ) {
    throw new Error('Outcome index must be an unsigned byte');
  }
  const [outcomeAddress] = deriveOutcomePda(params.market, params.outcomeIndex);
  const outcome = await fetchOutcome(connection, outcomeAddress);
  if (!outcome.market.equals(params.market) || outcome.outcomeIndex !== params.outcomeIndex) {
    throw new Error('Outcome account does not match the requested market and index');
  }
  const poolState = outcome.cpSwapPool;
  if (poolState === null) {
    throw new Error('Outcome has not migrated to a cp-swap pool');
  }
  const pool = await fetchGraduatedPoolInfoByPool(connection, {
    poolState,
    outcomeAddress,
    outcome,
    expectedQuoteMint: params.collateralMint,
  });
  const ammProgram = pool.ammProgramId;
  const quoteMint = pool.quoteMint;
  const outcomeMint = pool.outcomeMint;

  // Buy spends USDC for outcome tokens; sell does the reverse.
  const inputMint = params.direction === 'buy' ? quoteMint : outcomeMint;
  const outputMint = params.direction === 'buy' ? outcomeMint : quoteMint;
  const inputIsToken0 = inputMint.equals(pool.quoteTokenIndex === 0 ? quoteMint : outcomeMint);
  const inputVault = inputIsToken0 ? pool.token0Vault : pool.token1Vault;
  const outputVault = inputIsToken0 ? pool.token1Vault : pool.token0Vault;
  const inputTokenProgram = inputIsToken0 ? pool.token0Program : pool.token1Program;
  const outputTokenProgram = inputIsToken0 ? pool.token1Program : pool.token0Program;

  const program = await getCpSwapProgram(connection, ammProgram);
  const ataPayer = params.payer ?? params.user;
  // Idempotent ATA creation for both sides — the input ATA already holds the
  // user's balance; the output ATA may not exist yet (first buy/sell).
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, inputMint, inputTokenProgram),
    createIdempotentAtaIx(ataPayer, params.user, outputMint, outputTokenProgram),
  ];

  const ix = await invokeMethod(program, 'swapBaseInput', amountIn, minAmountOut)
    .accounts({
      payer: params.user,
      authority: deriveAmmAuthorityPda(ammProgram),
      ammConfig: pool.ammConfig,
      poolState,
      inputTokenAccount: deriveAta(params.user, inputMint, inputTokenProgram),
      outputTokenAccount: deriveAta(params.user, outputMint, outputTokenProgram),
      inputVault,
      outputVault,
      inputTokenProgram,
      outputTokenProgram,
      inputTokenMint: inputMint,
      outputTokenMint: outputMint,
      observationState: pool.observationState,
      eventAuthority: deriveEventAuthorityPda(ammProgram)[0],
      program: ammProgram,
    })
    .remainingAccounts([{ pubkey: outcomeAddress, isSigner: false, isWritable: false }])
    .instruction();

  return createBuildResult(
    [...preInstructions, ix],
    uniqueSignerKeys(params.user, ataPayer),
    params.computeUnits ?? estimateComputeUnits('amm_swap'),
  );
}

function validatedU64(
  value: BN,
  reason: 'amount_in_out_of_range' | 'min_amount_out_out_of_range',
): BN {
  if (value.isNeg() || value.gt(U64_MAX)) {
    throw new CpSwapValidationError(reason);
  }
  return value.clone();
}
